// Employee self-service: regularise one or several dates with a mandatory
// reason, optional evidence (PDF/JPG/PNG ≤ 5 MB) and per-date decisions.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FloppyDisk, PaperPlaneTilt, ArrowLeft, Paperclip, X, Plus } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";
import { REASONS, REQUEST_TYPES, EVIDENCE_ACCEPT, istToUtcIso, utcIsoToIstHHMM, uploadEvidence } from "../../lib/regularisation";

const ANOMALY_TO_TYPE = {
    absent_on_working_day: "full_day_attendance", missing_check_in: "missing_check_in",
    missing_check_out: "missing_check_out", missing_break_end: "missing_break_end",
    late_arrival: "late_arrival_excuse", early_departure: "early_departure_excuse", unclosed_break: "break_correction",
};
const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

function initialDates(params) {
    const list = (params.get("dates") || params.get("date") || "").split(",").map((s) => s.trim()).filter(Boolean);
    return list.length ? Array.from(new Set(list)).sort() : [todayIST()];
}

function DateChips({ dates, setDates, locked }) {
    const [add, setAdd] = useState("");
    return (
        <div>
            <Label className="text-xs">Dates ({dates.length})</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="rgz-date-chips">
                {dates.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 text-xs text-indigo-800" data-testid={`rgz-date-chip-${d}`}>
                        {formatDate(d)}
                        {!locked && dates.length > 1 && <button type="button" onClick={() => setDates(dates.filter((x) => x !== d))} aria-label={`Remove ${d}`} data-testid={`rgz-date-remove-${d}`}><X size={11} /></button>}
                    </span>
                ))}
            </div>
            {locked ? <p className="text-[11px] text-slate-500 mt-1">A draft keeps its date.</p> : (
                <div className="flex items-center gap-2 mt-2">
                    <Input type="date" max={todayIST()} value={add} onChange={(e) => setAdd(e.target.value)} className="h-9 w-[170px]" data-testid="rgz-date" />
                    <Button type="button" size="sm" variant="outline" disabled={!add} onClick={() => { setDates(Array.from(new Set([...dates, add])).sort()); setAdd(""); }} data-testid="rgz-add-date"><Plus size={13} className="mr-1" /> Add date</Button>
                </div>
            )}
        </div>
    );
}

function EvidenceField({ form, setForm }) {
    const [busy, setBusy] = useState(false);
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setBusy(true);
        try {
            const data = await uploadEvidence(file);
            setForm((f) => ({ ...f, evidence_object_id: data.object_id, evidence_filename: data.filename }));
            toast.success("Evidence attached.");
        } catch (err) {
            toast.error(err?.response ? formatApiError(err, "Upload failed") : err.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div>
            <Label className="text-xs">Evidence (optional · PDF, JPG or PNG · up to 5 MB)</Label>
            <div className="flex items-center gap-3 mt-1">
                <input type="file" accept={EVIDENCE_ACCEPT} onChange={onFile} disabled={busy} className="text-xs" data-testid="rgz-evidence-file" />
                {busy && <span className="text-xs text-slate-500">Uploading…</span>}
                {form.evidence_filename && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-700" data-testid="rgz-evidence-name">
                        <Paperclip size={12} /> {form.evidence_filename}
                        <button type="button" onClick={() => setForm((f) => ({ ...f, evidence_object_id: null, evidence_filename: null }))} data-testid="rgz-evidence-remove"><X size={11} /></button>
                    </span>
                )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Only you, your approver and Super Admin can open it.</p>
        </div>
    );
}

function ResultDialog({ result, onClose }) {
    return (
        <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="rgz-batch-result">
                <DialogHeader>
                    <DialogTitle>{result?.created.length} request{result?.created.length === 1 ? "" : "s"} submitted</DialogTitle>
                    <DialogDescription>Each date is decided separately by your approver.</DialogDescription>
                </DialogHeader>
                {result?.skipped.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1" data-testid="rgz-batch-skipped">
                        <div className="font-semibold">Not submitted:</div>
                        {result.skipped.map((s) => <div key={s.date}>{formatDate(s.date)} — {String(s.reason)}</div>)}
                    </div>
                )}
                <DialogFooter><Button onClick={onClose} data-testid="rgz-batch-result-ok">View my requests</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function RegularizationRequestForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const draftId = params.get("draft");
    const [dates, setDates] = useState(() => initialDates(params));
    const [form, setForm] = useState({
        request_type: ANOMALY_TO_TYPE[params.get("type")] || params.get("type") || "full_day_attendance",
        reason_code: "", reason_other: "", check_in_time: "", check_out_time: "", break_end_time: "",
        break_name: "", notes: "", justification: "", anomaly_id: params.get("anomaly_id") || null,
        evidence_object_id: null, evidence_filename: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    useEffect(() => {
        if (!draftId) return;
        api.get(`/regularization/request/${draftId}`).then(({ data: d }) => {
            const rc = d.requested_correction || {};
            setDates([d.attendance_date]);
            setForm({
                request_type: d.request_type, reason_code: d.reason_code || "", reason_other: d.reason_other || "",
                check_in_time: utcIsoToIstHHMM(rc.check_in_time), check_out_time: utcIsoToIstHHMM(rc.check_out_time),
                break_end_time: utcIsoToIstHHMM(rc.break_end_time), break_name: rc.break_name || "", notes: rc.notes || "",
                justification: d.justification || "", anomaly_id: d.anomaly_id || null,
                evidence_object_id: d.evidence_object_id || null, evidence_filename: d.evidence_filename || null,
            });
        }).catch((e) => toast.error(formatApiError(e, "Could not load the draft")));
    }, [draftId]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const fields = REQUEST_TYPES.find((t) => t.value === form.request_type)?.fields || [];

    const body = (draft) => {
        const d0 = dates[0];
        return {
            request_type: form.request_type,
            requested_correction: {
                check_in_time: fields.includes("check_in_time") ? istToUtcIso(form.check_in_time, d0) : null,
                check_out_time: fields.includes("check_out_time") ? istToUtcIso(form.check_out_time, d0) : null,
                break_end_time: fields.includes("break_end_time") ? istToUtcIso(form.break_end_time, d0) : null,
                break_name: form.break_name || null, notes: form.notes || null,
            },
            reason_code: form.reason_code || null, reason_other: form.reason_other || null,
            justification: form.justification, evidence_object_id: form.evidence_object_id,
            evidence_filename: form.evidence_filename, anomaly_id: dates.length === 1 ? form.anomaly_id : null,
            save_as_draft: draft,
        };
    };

    const validate = (draft) => {
        if (!dates.length) return "Add at least one date.";
        if (draft) return dates.length > 1 ? "Drafts can be saved for one date at a time." : null;
        if (!form.reason_code) return "Select a reason.";
        if (form.reason_code === "other" && form.reason_other.trim().length < 3) return "Describe the reason when you choose Other.";
        if (form.justification.trim().length < 10) return "Explanation must be at least 10 characters.";
        if (form.request_type === "time_correction" && !form.check_in_time && !form.check_out_time) return "Enter the corrected check-in and/or check-out time.";
        return null;
    };

    const submit = async (draft) => {
        const err = validate(draft);
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            if (draftId) {
                await api.patch(`/regularization/request/${draftId}`, { ...body(draft), attendance_date: dates[0] });
                if (!draft) await api.post(`/regularization/request/${draftId}/submit`);
                toast.success(draft ? "Draft updated." : "Draft submitted for approval.");
                navigate(`/regularization/my?id=${encodeURIComponent(draftId)}`);
            } else if (dates.length === 1) {
                const { data } = await api.post("/regularization/request", { ...body(draft), attendance_date: dates[0] });
                toast.success(draft ? `Draft saved (${data.id}).` : `Request ${data.id} submitted.`);
                navigate(`/regularization/my?id=${encodeURIComponent(data.id)}`);
            } else {
                const { data } = await api.post("/regularization/request/batch", { ...body(false), attendance_dates: dates });
                setResult(data);
            }
        } catch (e) {
            toast.error(formatApiError(e, "Submission failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl" data-testid="rgz-form-page">
            <PageHeader
                eyebrow="My Attendance & Regularisation"
                title={draftId ? "Edit draft request" : "Request regularisation"}
                description="Your reporting manager decides each date. Without a manager, Super Admin decides. You cannot approve your own request."
                actions={<Button variant="outline" onClick={() => navigate(-1)} data-testid="rgz-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>}
            />
            <div className="m-6 sm:m-8 bg-white rounded-xl border border-slate-200 p-5 space-y-5">
                <DateChips dates={dates} setDates={setDates} locked={!!draftId} />
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">What should the day show?</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.request_type} onChange={set("request_type")} data-testid="rgz-type">
                            {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Reason (required)</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.reason_code} onChange={set("reason_code")} data-testid="rgz-reason">
                            <option value="">Select a reason…</option>
                            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                    </div>
                </div>
                {form.reason_code === "other" && (
                    <div>
                        <Label className="text-xs">Specify the reason</Label>
                        <Input value={form.reason_other} maxLength={200} onChange={set("reason_other")} placeholder="e.g. Power cut at home office" data-testid="rgz-reason-other" />
                    </div>
                )}
                {fields.length > 0 && (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {fields.includes("check_in_time") && <div><Label className="text-xs">Check-in (IST)</Label><Input type="time" value={form.check_in_time} onChange={set("check_in_time")} data-testid="rgz-check-in" /></div>}
                        {fields.includes("check_out_time") && <div><Label className="text-xs">Check-out (IST)</Label><Input type="time" value={form.check_out_time} onChange={set("check_out_time")} data-testid="rgz-check-out" /></div>}
                        {fields.includes("break_end_time") && <div><Label className="text-xs">Break end (IST)</Label><Input type="time" value={form.break_end_time} onChange={set("break_end_time")} data-testid="rgz-break-end" /></div>}
                        {fields.includes("break_name") && <div><Label className="text-xs">Break name</Label><Input value={form.break_name} onChange={set("break_name")} placeholder="Lunch" data-testid="rgz-break-name" /></div>}
                        {dates.length > 1 && <p className="sm:col-span-2 text-[11px] text-slate-500">The same times are applied to every selected date.</p>}
                    </div>
                )}
                <div>
                    <Label className="text-xs">Explanation (required, at least 10 characters)</Label>
                    <Textarea rows={3} value={form.justification} onChange={set("justification")} placeholder="What happened and what you worked on." data-testid="rgz-justification" />
                </div>
                <EvidenceField form={form} setForm={setForm} />
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                    {dates.length === 1 && <Button variant="outline" disabled={submitting} onClick={() => submit(true)} data-testid="rgz-save-draft"><FloppyDisk size={14} className="mr-1" /> Save draft</Button>}
                    <Button disabled={submitting} onClick={() => submit(false)} data-testid="rgz-submit"><PaperPlaneTilt size={14} className="mr-1" /> Submit {dates.length > 1 ? `${dates.length} dates` : "for approval"}</Button>
                </div>
            </div>
            <ResultDialog result={result} onClose={() => navigate("/regularization/my")} />
        </div>
    );
}
